import { useState } from 'react';
import { Callout, TextField } from '@humanforce/mango-ui-react';
import { SectionCard } from '../components/SectionCard';
import { saveOrganisationOverview } from '../organisationApi';
import type { OrganisationFieldError, OrganisationPermissions } from '../organisationDetailsModel';
import type { OrgData } from '../organisationFixtures';

interface OverviewTabProps {
  orgId: string;
  orgData: OrgData;
  permissions: OrganisationPermissions;
}

type CardKey = 'identity' | 'contact' | 'defaults';

interface IdentityDraft {
  name: string;
  shortCode: string;
  country: string;
  fyEnd: string;
  hqAddress: string;
}

interface ContactDraft {
  primary: string;
  phone: string;
  supportEmail: string;
  senderEmail: string;
}

interface DefaultsDraft {
  timezone: string;
  currency: string;
}

const DisplayField = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="mgo-text-xs mgo-weight-strong mgo-color-muted" style={{ textTransform: 'uppercase' }}>{label}</div>
    <div className="mgo-text-s" style={{ marginTop: 'var(--mgo-space-05)' }}>{value}</div>
  </div>
);

export const OverviewTab = ({ orgId, orgData, permissions }: OverviewTabProps) => {
  const { overview } = orgData;

  const [editingCard, setEditingCard] = useState<CardKey | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<CardKey | null>(null);
  const [saveError, setSaveError] = useState<CardKey | null>(null);
  const [fieldErrors, setFieldErrors] = useState<OrganisationFieldError>({});

  const [identity, setIdentity] = useState<IdentityDraft>({ ...overview.identity });
  const [identityDraft, setIdentityDraft] = useState<IdentityDraft>({ ...overview.identity });

  const [contact, setContact] = useState<ContactDraft>({ ...overview.contact });
  const [contactDraft, setContactDraft] = useState<ContactDraft>({ ...overview.contact });

  const [defaults, setDefaults] = useState<DefaultsDraft>({ ...overview.defaults });
  const [defaultsDraft, setDefaultsDraft] = useState<DefaultsDraft>({ ...overview.defaults });

  const startEdit = (card: CardKey) => {
    if (!permissions.canEdit) return;
    if (card === 'identity') setIdentityDraft({ ...identity });
    if (card === 'contact') setContactDraft({ ...contact });
    if (card === 'defaults') setDefaultsDraft({ ...defaults });
    setEditingCard(card);
    setSaveSuccess(null);
    setSaveError(null);
    setFieldErrors({});
  };

  const cancelEdit = (card: CardKey) => {
    if (card === 'identity') setIdentityDraft({ ...identity });
    if (card === 'contact') setContactDraft({ ...contact });
    if (card === 'defaults') setDefaultsDraft({ ...defaults });
    setEditingCard(null);
    setFieldErrors({});
  };

  const saveEdit = async (card: CardKey) => {
    const payload =
      card === 'identity' ? { identity: identityDraft } :
        card === 'contact' ? { contact: contactDraft } :
          { defaults: defaultsDraft };

    try {
      const result = await saveOrganisationOverview(orgId, payload);
      if (!result.ok) {
        setSaveError(card);
        setSaveSuccess(null);
        setFieldErrors(result.errors ?? {});
        return;
      }
      if (card === 'identity') setIdentity({ ...identityDraft });
      if (card === 'contact') setContact({ ...contactDraft });
      if (card === 'defaults') setDefaults({ ...defaultsDraft });
      setEditingCard(null);
      setSaveSuccess(card);
      setSaveError(null);
      setFieldErrors({});
    } catch {
      setSaveError(card);
    }
  };

  const isEditing = (card: CardKey) => editingCard === card;

  if (!permissions.canView)
    return <p className="mgo-color-critical" style={{ margin: 0 }}>You do not have permission to view organisation details.</p>;

  return (
    <div style={{ alignItems: 'start', display: 'grid', gap: 'var(--mgo-space-40)', gridTemplateColumns: 'minmax(0, 1.65fr) minmax(0, 1fr)' }}>
      <div className="mgo-stack mgo-gap-l">
        <SectionCard
          icon="analytics-dashboard"
          isEditing={isEditing('identity')}
          subtitle="Identity settings"
          title={orgData.label}
          onCancel={() => cancelEdit('identity')}
          onEdit={permissions.canEdit ? () => startEdit('identity') : undefined}
          onSave={() => void saveEdit('identity')}
        >
          {isEditing('identity')
            ? (
              <>
                {saveError === 'identity' && <p className="mgo-color-critical" style={{ margin: 0 }}>Save failed. Please try again.</p>}
                {fieldErrors.retroactiveReason && <p className="mgo-color-critical" style={{ margin: 0 }}>{fieldErrors.retroactiveReason}</p>}
                <div className="mgo-grid mgo-grid-cols-2 mgo-gap-s">
                  <div className="mgo-col-span-2">
                    <TextField label="Organisation name" value={identityDraft.name} onChange={(e) => setIdentityDraft((d) => ({ ...d, name: e.target.value }))} />
                  </div>
                  <TextField label="Short code" value={identityDraft.shortCode} onChange={(e) => setIdentityDraft((d) => ({ ...d, shortCode: e.target.value }))} />
                  <label className="mgo-stack mgo-gap-xs mgo-text-s mgo-weight-strong mgo-color-neutral">
                    Primary country
                    <select className="select-control" value={identityDraft.country ?? ''} onChange={(e) => setIdentityDraft((d) => ({ ...d, country: e.target.value }))}>
                      <option value="AU">AU</option>
                      <option value="NZ">NZ</option>
                      <option value="GB">GB</option>
                    </select>
                  </label>
                  <label className="mgo-stack mgo-gap-xs mgo-text-s mgo-weight-strong mgo-color-neutral">
                    Financial year ends
                    <select className="select-control" value={identityDraft.fyEnd ?? ''} onChange={(e) => setIdentityDraft((d) => ({ ...d, fyEnd: e.target.value }))}>
                      <option value="JUN-30">JUN-30</option>
                      <option value="MAR-31">MAR-31</option>
                      <option value="APR-05">APR-05</option>
                    </select>
                  </label>
                  <div className="mgo-col-span-2">
                    <TextField label="HQ address" value={identityDraft.hqAddress} onChange={(e) => setIdentityDraft((d) => ({ ...d, hqAddress: e.target.value }))} />
                  </div>
                </div>
              </>
            )
            : (
              <>
                {saveSuccess === 'identity' && <p className="mgo-color-positive" style={{ margin: 0 }}>Saved successfully.</p>}
                <div className="mgo-grid mgo-grid-cols-2 mgo-gap-s">
                  <div className="mgo-col-span-2"><DisplayField label="Organisation name" value={identity.name} /></div>
                  <DisplayField label="Short code" value={identity.shortCode} />
                  <DisplayField label="Primary country" value={identity.country} />
                  <DisplayField label="Financial year ends" value={identity.fyEnd} />
                  <div className="mgo-col-span-2"><DisplayField label="HQ address" value={identity.hqAddress} /></div>
                </div>
              </>
            )
          }
        </SectionCard>

        <SectionCard
          icon="person"
          isEditing={isEditing('contact')}
          title="Contact details"
          onCancel={() => cancelEdit('contact')}
          onEdit={permissions.canEdit ? () => startEdit('contact') : undefined}
          onSave={() => void saveEdit('contact')}
        >
          {isEditing('contact')
            ? (
              <>
                {saveError === 'contact' && <p className="mgo-color-critical" style={{ margin: 0 }}>Save failed. Please try again.</p>}
                <div className="mgo-grid mgo-grid-cols-2 mgo-gap-s">
                  <TextField label="Primary contact" value={contactDraft.primary} onChange={(e) => setContactDraft((d) => ({ ...d, primary: e.target.value }))} />
                  <TextField label="Phone" value={contactDraft.phone} onChange={(e) => setContactDraft((d) => ({ ...d, phone: e.target.value }))} />
                  <TextField label="Support email" type="email" value={contactDraft.supportEmail} onChange={(e) => setContactDraft((d) => ({ ...d, supportEmail: e.target.value }))} />
                  <TextField label="Workflow sender email" type="email" value={contactDraft.senderEmail} onChange={(e) => setContactDraft((d) => ({ ...d, senderEmail: e.target.value }))} />
                </div>
              </>
            )
            : (
              <>
                {saveSuccess === 'contact' && <p className="mgo-color-positive" style={{ margin: 0 }}>Saved successfully.</p>}
                <div className="mgo-grid mgo-grid-cols-2 mgo-gap-s">
                  <DisplayField label="Primary contact" value={contact.primary} />
                  <DisplayField label="Phone" value={contact.phone} />
                  <DisplayField label="Support email" value={contact.supportEmail} />
                  <DisplayField label="Workflow sender email" value={contact.senderEmail} />
                </div>
              </>
            )
          }
        </SectionCard>
      </div>

      <div className="mgo-stack mgo-gap-l">
        <SectionCard
          icon="settings"
          isEditing={isEditing('defaults')}
          title="Default settings"
          onCancel={() => cancelEdit('defaults')}
          onEdit={permissions.canEdit ? () => startEdit('defaults') : undefined}
          onSave={() => void saveEdit('defaults')}
        >
          {isEditing('defaults')
            ? (
              <>
                {saveError === 'defaults' && <p className="mgo-color-critical" style={{ margin: 0 }}>Save failed. Please try again.</p>}
                <div className="mgo-stack mgo-gap-s">
                  <label className="mgo-stack mgo-gap-xs mgo-text-s mgo-weight-strong mgo-color-neutral">
                    Default timezone
                    <select className="select-control" value={defaultsDraft.timezone ?? ''} onChange={(e) => setDefaultsDraft((d) => ({ ...d, timezone: e.target.value }))}>
                      <option value="Australia/Sydney">Australia/Sydney</option>
                      <option value="Australia/Brisbane">Australia/Brisbane</option>
                      <option value="Australia/Melbourne">Australia/Melbourne</option>
                      <option value="Pacific/Auckland">Pacific/Auckland</option>
                      <option value="Europe/London">Europe/London</option>
                    </select>
                  </label>
                  <label className="mgo-stack mgo-gap-xs mgo-text-s mgo-weight-strong mgo-color-neutral">
                    Default currency
                    <select className="select-control" value={defaultsDraft.currency ?? ''} onChange={(e) => setDefaultsDraft((d) => ({ ...d, currency: e.target.value }))}>
                      <option value="AUD">AUD</option>
                      <option value="NZD">NZD</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </label>
                </div>
              </>
            )
            : (
              <>
                {saveSuccess === 'defaults' && <p className="mgo-color-positive" style={{ margin: 0 }}>Saved successfully.</p>}
                <div className="mgo-stack mgo-gap-s">
                  <DisplayField label="Default timezone" value={defaults.timezone} />
                  <DisplayField label="Default currency" value={defaults.currency} />
                </div>
              </>
            )
          }
        </SectionCard>

        <Callout tone={overview.callout.tone === 'brand' ? 'info' : overview.callout.tone} title={overview.callout.title}>
          {overview.callout.text}
        </Callout>
      </div>
    </div>
  );
};
